import { FC, MouseEventHandler, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select, {
  ClearIndicatorProps,
  MenuListProps,
  MenuProps,
} from "react-select";
import useEvent from "react-use-event-hook";

import { SVG_MUI } from "@ryde/assets";
import {
  FilterDropdownOption,
  FilterDropdownWrapper,
  FilterMenuList,
  FilterMultiselectMenu,
  FilterSelectControl,
  FramerTooltip,
  MenuButton,
  RemoveFilteringIcon,
  selectStyles,
  selectStylesMobile,
  StyledLabelBlack,
  StyledLabelBlue,
} from "@ryde/controls";
import { useRtl } from "@ryde/redux";
import { Log, stringifyDropdownItems, useIsMobile } from "@ryde/shared";

import { IFilterMultiReportsProps } from "./filter-multiselect.reports.models";
import { useFilterMultiselectReports } from "./use-filter-multiselect.reports";
import { useTableFiltersActionsReports } from "./use-table-filters-actions-reports";

export const FilterMultiSelectReports: FC<IFilterMultiReportsProps> = props => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const {
    value,
    buttonContent,
    multiselectLabel,
    onItemsCheckChange,
    dropdownItems,
    isOpen,
    setIsOpen,
    selectAll,
    isSelectedAll,
    input,
    onInputChange,
    filteredDropDownItems,
  } = useFilterMultiselectReports(props);
  const { templateKey, onRemove, isSingleValue } = props;

  const handleToggle = useEvent(() => setIsOpen(prev => !prev));
  const handleClose = useEvent(() => setIsOpen(false));
  const { handleRemoveFilter } = useTableFiltersActionsReports();
  const removeFilter: MouseEventHandler = useEvent(e => {
    e.stopPropagation();
    handleClose();

    if (!templateKey) {
      Log.warn(t("errors.cantRemoveFilterInvalidFieldId"));
      return;
    }

    if (onRemove) {
      onRemove({ templateKey });
      return;
    }

    handleRemoveFilter(templateKey);
  });

  const stylesComputed = useMemo(() => {
    return isMobile ? selectStylesMobile : selectStyles;
  }, [isMobile]);

  const { isRtl } = useRtl();
  let tooltipText = t("general.FILTERED_BY_ENTITY", {
    entity: multiselectLabel,
  });

  if (value.length > 0) {
    tooltipText += " : " + stringifyDropdownItems(value);
  }

  return (
    <FilterDropdownWrapper
      dropdownHasSelectedItems={!!value?.length}
      isFilterItem={true}
      isOpen={isOpen}
      onClose={handleClose}
      target={
        <FramerTooltip content={tooltipText} mouseEnterDelay={900}>
          <MenuButton
            onClick={handleToggle}
            text={multiselectLabel}
            labelBlack={buttonContent.labelBlack}
            labelBlue={buttonContent.labelBlue}
            imageAfterText
            disabled={!dropdownItems.length}
          >
            <StyledLabelBlack>{buttonContent.labelBlack}</StyledLabelBlack>
            <StyledLabelBlue>{buttonContent.labelBlue}</StyledLabelBlue>
            <SVG_MUI.CircleXMark
              className="circle-x-mark"
              onClick={e => removeFilter(e)}
            />
          </MenuButton>
        </FramerTooltip>
      }
    >
      <Select
        value={value}
        isMulti
        // eslint-disable-next-line jsx-a11y-x/no-autofocus -- intentional: focus the field when opened by user action
        autoFocus
        backspaceRemovesValue={false}
        components={{
          DropdownIndicator: null,
          IndicatorSeparator: null,
          Control: FilterSelectControl,
          Option: FilterDropdownOption,
          ClearIndicator: ClearIndicator,
          Menu: menuProps => (
            <FilterMultiselectMenu
              selectAll={selectAll}
              isSelectedAll={isSelectedAll}
              isSelectAllOptionEnabled={!isSingleValue}
              {...(menuProps as MenuProps)}
              children={menuProps.children}
              isRtl={isRtl}
            />
          ),

          MenuList: menuProps => (
            <FilterMenuList
              {...(menuProps as MenuListProps)}
              filteredOptions={
                (filteredDropDownItems ??
                  dropdownItems) as MenuListProps["options"]
              }
            />
          ),
        }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={true}
        menuIsOpen
        options={dropdownItems}
        onChange={onItemsCheckChange as (newValue: unknown) => void}
        placeholder={`${t(`general.SEARCH`)}...`}
        styles={stylesComputed}
        tabSelectsValue={false}
        inputValue={input}
        onInputChange={onInputChange}
      />
    </FilterDropdownWrapper>
  );
};

/*
 * Typed with a plain boolean rather than `true`, to agree with the shared filter components this Select is
 * built from. They all feed the same IsMulti parameter, and a mix of `true` and `boolean` leaves the
 * inference to settle whichever way the program happens to push it.
 */
export const ClearIndicator = (
  props: ClearIndicatorProps<unknown, boolean>,
): JSX.Element => {
  const {
    children,
    innerProps: { ref, ...restInnerProps },
  } = props;

  return (
    <div {...restInnerProps} ref={ref}>
      <div style={{ marginInlineEnd: "5px" }}>
        <RemoveFilteringIcon />
      </div>
      {children}
    </div>
  );
};
